import { ReactNode, useState } from 'react';
import { Box, styled } from '@mui/material';
import { ReactComponent as ChevronDownIcon } from '../../../assets/icons/chevron-down.svg';
import { ReactComponent as ChevronUpIcon } from '../../../assets/icons/chevron-up.svg';

export interface AccordionItemData {
    id: string;
    title: ReactNode;
    content: ReactNode;
}

export interface AccordionProps {
    items: AccordionItemData[];
    /** Allow more than one panel to be expanded simultaneously. Defaults to single-expand. */
    allowMultiple?: boolean;
    /** Ids expanded on first render when the component is uncontrolled. */
    defaultExpandedIds?: string[];
    /** Controlled set of expanded ids. When provided, the parent owns the expanded state. */
    expandedIds?: string[];
    /** Called with the next set of expanded ids whenever an item is toggled. */
    // eslint-disable-next-line no-unused-vars
    onChange?: (expandedIds: string[]) => void;
    className?: string;
}

const AccordionList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    width: '100%'
}));

const AccordionItemWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '100%',
    backgroundColor: theme.palette.semantic.bg.default,
    borderRadius: '12px',
    overflow: 'hidden'
}));

const AccordionHeader = styled('button', { shouldForwardProp: (prop) => prop !== 'expanded' })<{
    expanded: boolean;
}>(({ theme, expanded }) => ({
    appearance: 'none',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: '28px',
    padding: theme.spacing(0, 2),
    background: 'none',
    border: 'none',
    borderBottom: expanded ? `1px solid ${theme.palette.semantic.bd.default}` : 'none',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: '"TT Commons Pro"',
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '22px',
    color: theme.palette.semantic.int.selected,
    '&:focus-visible': {
        outline: `2px solid ${theme.palette.semantic.int.focused}`,
        outlineOffset: '2px'
    }
}));

const ChevronSlot = styled('span')({
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    width: '24px',
    height: '28px',
    paddingTop: '4px'
});

const AccordionContent = styled(Box)(({ theme }) => ({
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: theme.spacing(2),
    borderRadius: '6px'
}));

const AccordionText = styled('div')(({ theme }) => ({
    margin: 0,
    flex: '1 1 0',
    minWidth: 0,
    fontFamily: '"TT Commons Pro"',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '22px',
    color: theme.palette.semantic.text.default
}));

export const Accordion = ({
    items,
    allowMultiple = false,
    defaultExpandedIds,
    expandedIds,
    onChange,
    className
}: AccordionProps) => {
    const isControlled = expandedIds !== undefined;
    const [internalExpandedIds, setInternalExpandedIds] = useState<string[]>(defaultExpandedIds ?? []);
    const currentExpandedIds = isControlled ? expandedIds : internalExpandedIds;

    const toggleItem = (id: string) => {
        const isExpanded = currentExpandedIds.includes(id);
        let next: string[];
        if (allowMultiple) {
            next = isExpanded ? currentExpandedIds.filter((itemId) => itemId !== id) : [...currentExpandedIds, id];
        } else {
            next = isExpanded ? [] : [id];
        }
        if (!isControlled) {
            setInternalExpandedIds(next);
        }
        onChange?.(next);
    };

    return (
        <AccordionList className={className}>
            {items.map(({ id, title, content }) => {
                const expanded = currentExpandedIds.includes(id);
                return (
                    <AccordionItemWrapper key={id}>
                        <AccordionHeader
                            type="button"
                            expanded={expanded}
                            id={`accordion-header-${id}`}
                            aria-expanded={expanded}
                            aria-controls={`accordion-panel-${id}`}
                            onClick={() => toggleItem(id)}
                        >
                            {title}
                            <ChevronSlot>
                                {expanded ? (
                                    <ChevronUpIcon aria-hidden="true" />
                                ) : (
                                    <ChevronDownIcon aria-hidden="true" />
                                )}
                            </ChevronSlot>
                        </AccordionHeader>
                        {expanded && (
                            <AccordionContent
                                id={`accordion-panel-${id}`}
                                role="region"
                                aria-labelledby={`accordion-header-${id}`}
                            >
                                <AccordionText>{content}</AccordionText>
                            </AccordionContent>
                        )}
                    </AccordionItemWrapper>
                );
            })}
        </AccordionList>
    );
};
